<template>
  <div class="flex flex-col bg-white rounded-xl border border-stone-200 shadow-md p-6">
    <div class="flex items-center gap-3 mb-3">
      <span class="flex items-center justify-center w-10 h-10 rounded-full bg-bpblue/10 text-bpblue shrink-0">
        <font-awesome-icon icon="user-graduate" class="h-5 w-5" />
      </span>
      <div class="min-w-0 flex-1">
        <h3 class="text-base font-semibold text-stone-800 leading-5">{{ $t('dashboard.kb_chatbot_title') }}</h3>
      </div>
    </div>

    <p v-if="!loading && status === null"
       class="max-w-[95%] mb-3 rounded-2xl rounded-bl-sm bg-stone-100 px-4 py-3 text-sm leading-6 text-stone-700">{{ $t('dashboard.kb_chatbot_welcome') }}</p>

    <div class="flex flex-wrap gap-2 mb-4">
      <button v-for="(suggestion, i) in $tm('dashboard.kb_chatbot_suggestions')" :key="i" type="button"
              :disabled="loading" @click="useSuggestion($rt(suggestion))"
              class="rounded-full border border-stone-200 bg-bpblue/5 px-3 py-1 text-xs font-medium text-bpblue hover:bg-bpblue/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
        {{ $rt(suggestion) }}
      </button>
    </div>

    <div v-if="loading || status !== null" class="flex-1 space-y-3 mb-4 text-sm leading-6 text-stone-700" aria-live="polite">
      <div v-if="lastQuestion && (loading || status !== null)" class="flex justify-end">
        <p class="max-w-[85%] rounded-2xl rounded-br-sm bg-bpblue px-4 py-2 text-white">{{ lastQuestion }}</p>
      </div>

      <div v-if="loading" class="inline-flex items-center gap-1.5 rounded-2xl rounded-bl-sm bg-stone-100 px-4 py-3"
           :aria-label="$t('dashboard.kb_chatbot_loading')">
        <span class="h-2 w-2 rounded-full bg-stone-400 animate-bounce"></span>
        <span class="h-2 w-2 rounded-full bg-stone-400 animate-bounce" style="animation-delay: 150ms"></span>
        <span class="h-2 w-2 rounded-full bg-stone-400 animate-bounce" style="animation-delay: 300ms"></span>
      </div>

      <p v-else-if="status === 'answered'" class="max-w-[95%] rounded-2xl rounded-bl-sm bg-stone-100 px-4 py-3 whitespace-pre-line">{{ answer }}</p>

      <div v-else-if="status === 'unanswered' || status === 'limit'" class="max-w-[95%] rounded-2xl rounded-bl-sm bg-stone-100 px-4 py-3">
        <p>{{ $t('dashboard.kb_chatbot_unanswered') }}</p>
        <a :href="ticketUrl"
           class="mt-3 inline-flex items-center gap-2 rounded-lg bg-bpblue px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-bpbluedeep transition-colors">
          <font-awesome-icon icon="envelope" class="h-4 w-4" />
          {{ $t('dashboard.kb_chatbot_open_ticket') }}
        </a>
      </div>

      <div v-else-if="status === 'retry'" class="rounded-2xl bg-amber-50 px-4 py-3 text-amber-800">
        <p>{{ $t('dashboard.kb_chatbot_retry') }}</p>
        <button type="button" @click="question = lastQuestion; ask()" :disabled="loading"
                class="mt-3 inline-flex items-center gap-2 rounded-lg bg-bpblue px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-bpbluedeep transition-colors disabled:opacity-50">
          {{ $t('dashboard.kb_chatbot_retry_button') }}
        </button>
      </div>

      <p v-else-if="status === 'error'" class="rounded-2xl bg-red-50 px-4 py-3 text-red-700">{{ $t('dashboard.kb_chatbot_error') }}</p>
    </div>

    <form class="flex gap-2 mt-auto" @submit.prevent="ask">
      <input ref="input" v-model="question" type="text" maxlength="500" :disabled="loading"
             :placeholder="$t('dashboard.kb_chatbot_placeholder')"
             class="flex-1 min-w-0 rounded-lg border border-stone-300 px-4 py-2.5 text-sm placeholder-stone-400 focus:border-bpblue focus:outline-none focus:ring-2 focus:ring-bpblue/30 disabled:bg-stone-50" />
      <button type="submit" :disabled="loading || !question.trim()"
              class="rounded-lg bg-bpblue px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-bpbluedeep transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
        {{ $t('dashboard.kb_chatbot_send') }}
      </button>
    </form>
  </div>
</template>

<script>
import {useAcademiesStore} from "@/stores/academies";
import KbChatbotService from "@/services/kb/KbChatbotService";

export default {
  name: "KbChatbotWidget",
  data() {
    return {
      question: '',
      lastQuestion: '',
      answer: '',
      // null | answered | unanswered | limit | retry | error
      status: null,
      remaining: null,
      loading: false,
    }
  },
  setup() {
    return {academyStore: useAcademiesStore()}
  },
  computed: {
    // Placeholder hasta tener sistema de tickets: abre un correo a soporte.
    ticketUrl() {
      return 'mailto:soporte@brainspro.com?subject=' + encodeURIComponent(this.$t('dashboard.kb_chatbot_ticket_subject'))
          + '&body=' + encodeURIComponent(this.lastQuestion || '')
    },
  },
  methods: {
    // Rellena el input con la pregunta sugerida, sin enviarla
    useSuggestion(text) {
      this.question = text
      this.$nextTick(() => this.$refs.input.focus())
    },
    ask() {
      const question = this.question.trim()
      if (!question || this.loading) return

      this.loading = true
      this.lastQuestion = question

      KbChatbotService.ask(this.academyStore.academy.uuid, question)
          .then((response) => {
            // remaining va en el nivel superior; answer y was_answered dentro de data
            this.remaining = response.data.remaining ?? this.remaining
            this.answer = response.data.data.answer
            this.status = response.data.data.was_answered ? 'answered' : 'unanswered'
            this.question = ''
          })
          .catch((error) => {
            if (error?.response?.status === 429) {
              this.remaining = 0
              this.status = 'limit'
            } else if (error?.response?.status === 503 && error?.response?.data?.error === 'technical_error') {
              this.remaining = error.response.data.remaining ?? this.remaining
              this.status = 'retry'
            } else {
              this.status = 'error'
            }
          })
          .finally(() => {
            this.loading = false
          })
    },
  },
}
</script>
